import { BayLocationOrder } from '@warebee/frontend/data-access-api-graphql';
import { OrderTypeOption } from '@warebee/shared/data-access-layout-import-converter';
import classNames from 'classnames';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { Button } from '../../../../components/actions/Button';
import DropdownSelector from '../../../../components/actions/DropdownSelector';
import InputNumber from '../../../../components/inputs/InputNumber';
import { Container } from '../../../../components/layout/ContainerFlex';
import { Spacer } from '../../../../components/layout/Spacer';
import { ActionBar } from '../../../../components/nav/ActionBar';
import Tag from '../../../../helpContext/Tag';
import { converterAllAreasBuilder } from '../store/converter.state';
import {
  ConverterAisleTableRow,
  getOrderOptionsMap,
} from '../store/converter.types';

export type ConverterUpdateAisleTableActionProps = {
  selectedAisles: ConverterAisleTableRow[];
};

const ConverterUpdateAisleTableAction: React.FC<
  ConverterUpdateAisleTableActionProps
> = props => {
  const { t } = useTranslation('designer');
  const [areaBuilders, setAreaBuilders] = useRecoilState(
    converterAllAreasBuilder,
  );
  const [aisleWidth, setAisleWidth] = useState<number>(null);
  const [leftBaysOrderType, setLeftBaysOrderType] =
    useState<OrderTypeOption>(null);
  const [rightBaysOrderType, setRightBaysOrderType] =
    useState<OrderTypeOption>(null);
  const [leftLocationsOrder, setLeftLocationsOrder] =
    useState<BayLocationOrder>(null);
  const [rightLocationsOrder, setRightLocationsOrder] =
    useState<BayLocationOrder>(null);

  const orderOptions = getOrderOptionsMap(t);
  const orderOptionsKeys = [...orderOptions.keys()].filter(
    o => o === OrderTypeOption.Default || o === OrderTypeOption.Reverse,
  );

  function updateAisles() {
    const newAreaBuilders = { ...areaBuilders };

    const affectedAreas: string[] = _(props.selectedAisles)
      .map(s => s.areaId)
      .uniq()
      .value();

    _.forEach(affectedAreas, affectedAreaId => {
      const newAisleSettings = {
        ...newAreaBuilders[affectedAreaId].aisleSettings,
      };

      _(props.selectedAisles)
        .filter(aisleRow => aisleRow.areaId === affectedAreaId)
        .forEach(aisleRow => {
          const current = newAisleSettings[aisleRow.id];
          newAisleSettings[aisleRow.id] = {
            ...current,
            width: aisleWidth,
            bayRowSettings: {
              LEFT: {
                ...current?.bayRowSettings?.LEFT,
                orderType:
                  leftBaysOrderType ?? current?.bayRowSettings?.LEFT?.orderType,
              },
              RIGHT: {
                ...current?.bayRowSettings?.RIGHT,
                orderType:
                  rightBaysOrderType ??
                  current?.bayRowSettings?.RIGHT?.orderType,
              },
            },
            locationOrderSettings: {
              LEFT: leftLocationsOrder ?? current?.locationOrderSettings?.LEFT,
              RIGHT:
                rightLocationsOrder ?? current?.locationOrderSettings?.RIGHT,
            },
          };
        });

      newAreaBuilders[affectedAreaId] = {
        ...newAreaBuilders[affectedAreaId],
        aisleSettings: newAisleSettings,
      };
    });
    setAreaBuilders(newAreaBuilders);
  }

  const selectedAislesCount = _.size(props.selectedAisles);

  return (
    // <Sidebar className="relative">
    <Container
      fullWidth={false}
      col
      overflow
      className="bg-menu/30 border-menu relative min-h-24 min-w-[16rem] border-l"
    >
      <ActionBar sticky stickyTop className="text-xxs gap-1 p-2 py-3 uppercase">
        {/* <span className="flex-1">
          {t('Batch Update selected:', {
            selectedAislesCount: selectedAislesCount,
          })}
        </span>
        <Tag className="bg-menu-active text-menu-active-text mx-2 text-sm">
          {selectedAislesCount}
        </Tag> */}

        <Spacer flexspace />

        <Button
          label={t('Batch Update bays:', {
            selectedAislesCount: selectedAislesCount,
          })}
          full
          buttonType="primary"
          buttonSize={'xs'}
          className="mx-2 rounded"
          isDisabled={selectedAislesCount === 0}
          onPress={updateAisles}
        >
          <Tag className="text-menu-active-text bg-transparent px-0.5 text-sm">
            {selectedAislesCount}
          </Tag>
        </Button>
      </ActionBar>

      <div className="flex flex-1 flex-col justify-items-start gap-6 p-4">
        <InputNumber
          title={t`Width`}
          className="flex-none"
          inputClassName="text-xs"
          value={aisleWidth}
          onChange={v => setAisleWidth(v)}
          range={[0, null]}
          debounceInterval={100}
          placeholder={t`New Width`}
        />

        <DropdownSelector
          classNameLabel={classNames('text-xs flex-1 text-menu-text')}
          className={classNames('text-xs')}
          panelMode
          widthFull
          value={leftBaysOrderType}
          label={t`Left Bay Order`}
          values={[null, ...orderOptionsKeys]}
          renderValue={i => (_.isNil(i) ? t`As is` : orderOptions.get(i))}
          onChange={(v: OrderTypeOption) => {
            setLeftBaysOrderType(v);
          }}
        />

        <DropdownSelector
          classNameLabel={classNames('text-xs flex-1 text-menu-text')}
          className={classNames('text-xs')}
          panelMode
          widthFull
          value={leftLocationsOrder}
          values={[null, 'LTR', 'RTL']}
          label={t(`Left Bay's location order`)}
          renderValue={i => i ?? t`As is`}
          onChange={(v: BayLocationOrder) => {
            setLeftLocationsOrder(v);
          }}
        />

        <DropdownSelector
          classNameLabel={classNames('text-xs flex-1 text-menu-text')}
          className={classNames('text-xs')}
          panelMode
          widthFull
          value={rightBaysOrderType}
          label={t`Right Bay Order`}
          values={[null, ...orderOptionsKeys]}
          renderValue={i => (_.isNil(i) ? t`As is` : orderOptions.get(i))}
          onChange={(v: OrderTypeOption) => {
            setRightBaysOrderType(v);
          }}
        />

        <DropdownSelector
          classNameLabel={classNames('text-xs flex-1 text-menu-text')}
          className={classNames('text-xs')}
          panelMode
          widthFull
          value={rightLocationsOrder}
          values={[null, 'LTR', 'RTL']}
          label={t(`Right Bay's location order`)}
          renderValue={i => i ?? t`As is`}
          onChange={(v: BayLocationOrder) => {
            setRightLocationsOrder(v);
          }}
        />
      </div>
    </Container>
  );
};

export default ConverterUpdateAisleTableAction;
